/**
 * Screenshot Preview Component
 * Displays screenshots as inline images with lightbox capability
 */

import React, { useState } from 'react';
import {
  Box,
  Group,
  ActionIcon,
  Tooltip,
  Modal,
  Stack,
  Text,
  Button,
} from '@mantine/core';
import { IconDownload, IconX, IconMaximize } from '@tabler/icons-react';
import styles from './ScreenshotPreview.module.css';

interface ScreenshotPreviewProps {
  /** Image URL (e.g. /api/assistant/attachments/:id or blob:) */
  src: string;
  /** Alternative text */
  alt?: string;
  /** Thumbnail size in pixels */
  thumbnailSize?: number;
  /** On download callback */
  onDownload?: () => void;
  /** Filename for download */
  filename?: string;
}

export const ScreenshotPreview: React.FC<ScreenshotPreviewProps> = ({
  src,
  alt = 'Screenshot',
  thumbnailSize = 200,
  onDownload,
  filename = 'screenshot.png',
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else if (src) {
      // Fallback: direct download
      const link = document.createElement('a');
      link.href = src;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      {/* Thumbnail */}
      <Box className={styles.thumbnailContainer}>
        <div
          className={styles.thumbnail}
          onClick={() => setLightboxOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setLightboxOpen(true);
            }
          }}
          style={{
            width: `${thumbnailSize}px`,
            height: `${thumbnailSize}px`,
          }}
        >
          <img
            src={src}
            alt={alt}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
            }}
          />

          {/* Overlay on hover */}
          <div className={styles.overlay}>
            <Tooltip label="Vollbild anzeigen" withArrow>
              <ActionIcon
                size="lg"
                radius="xl"
                variant="filled"
                color="gray"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxOpen(true);
                }}
                aria-label="Vollbild öffnen"
              >
                <IconMaximize size={20} />
              </ActionIcon>
            </Tooltip>
          </div>
        </div>

        {/* Download button */}
        <Tooltip label="Herunterladen" withArrow>
          <ActionIcon
            size="sm"
            variant="light"
            onClick={handleDownload}
            className={styles.downloadButton}
            aria-label="Screenshot herunterladen"
          >
            <IconDownload size={16} />
          </ActionIcon>
        </Tooltip>
      </Box>

      {/* Lightbox Modal */}
      <Modal
        opened={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        fullScreen
        padding={0}
        styles={{
          content: {
            backgroundColor: '#000',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <Stack gap="md" align="center" justify="center" w="100%" h="100%">
          {/* Fullscreen Image */}
          <img
            src={src}
            alt={alt}
            style={{
              maxWidth: '90vw',
              maxHeight: '80vh',
              objectFit: 'contain',
            }}
          />

          {/* Controls */}
          <Group gap="md">
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={handleDownload}
              variant="default"
            >
              Herunterladen
            </Button>

            <ActionIcon
              size="lg"
              variant="default"
              onClick={() => setLightboxOpen(false)}
              aria-label="Lightbox schließen"
            >
              <IconX size={20} />
            </ActionIcon>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

ScreenshotPreview.displayName = 'ScreenshotPreview';
